// SOURCES tab — the real pane (F-SOURCES). Rebuilds the throwaway mockup
// `ui/panes/PaneSources` as a Tailwind + logical-RTL + i18n surface that plugs
// into the F-SHELL registry (see `register.ts`). It reuses the already-wired
// hooks VERBATIM (`useSources`/`useCite`/`useCaseSources`/`usePreflight`) — no
// new BE wire, no hook rewrite.
//
// Invariants:
//   W1 — no business logic here. Search ranking, cite resolution, pièce
//        numbering all stay BE/hook-side. The pane only projects + renders.
//   W7 — citation status comes from `usePreflight`/`useCite`, never computed.
//   W2 — no Office.js; the cite path goes via `useCite` → adapter.
//   §9 — logical RTL utilities only; BE-emitted Arabic fragment text gets
//        per-string direction via `bidiProps` inside `SourceCard`.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSources } from '@/ui/hooks/use-sources'
import { useCite } from '@/ui/hooks/use-cite'
import { useCaseSources } from '@/ui/hooks/use-case-sources'
import { usePreflight } from '@/ui/hooks/use-preflight'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { renderPieceLabel } from '@/domain/case-refs/usecases/render-piece'
import { SourceSearchBox } from './SourceSearchBox'
import { SourceCard, type SourceCardData } from './SourceCard'
import { SourceGroup } from './SourceGroup'
import type { SourceResult } from '@/domain/sources/entity'
import type { CaseFact, FactStatus } from '@/domain/case/entity'
import type { PieceRef } from '@/domain/case-refs/entity'
import type { ChipLanguage } from '@/domain/citation-chip/entity'

// Map a jurisdiction code to a flag token. Only FR/MA have a flag in the
// design tokens today; everything else renders no flag. Presentation
// derivation — never a `family` value (country-code clean-cut).
function flagOf(countryCode: string): 'fr' | 'ma' | undefined {
  const k = countryCode.toLowerCase()
  return k === 'fr' ? 'fr' : k === 'ma' ? 'ma' : undefined
}

export function SourcesPane(): JSX.Element {
  const { t } = useTranslation()
  const { articles, jurisprudence, loading, error, search } = useSources()
  const { cite, citePiece, inserting } = useCite()
  const caseSources = useCaseSources()
  const activeCaseId = useCaseStore((s) => s.activeContext?.caseId ?? null)
  // Brief language drives the « Pièce n° N » string (presentation). Falls
  // back to 'fr' for an unbound doc.
  const language: ChipLanguage = useCaseStore(
    (s) => s.activeContext?.lang ?? 'fr',
  )
  // autoRun:false — opening Sources must NOT burn a preflight call; it runs
  // only on the explicit "Vérifier tout" button.
  const preflight = usePreflight({ autoRun: false })

  // Load the active case's faits/pièces when a case becomes active.
  const loadCaseSources = caseSources.load
  useEffect(() => {
    if (activeCaseId) void loadCaseSources()
  }, [activeCaseId, loadCaseSources])

  const hasCorpus = articles.length > 0 || jurisprudence.length > 0
  const hasCaseSources =
    caseSources.faits.length > 0 || caseSources.pieces.length > 0
  const hasAnything = hasCorpus || hasCaseSources
  // Merge the two verify summaries (presentation): article/ECLI issues
  // (broken + stale) + pièce bordereau issues (not-promoted + orphan).
  const unverified =
    preflight.report.broken.length +
    preflight.report.stale.length +
    preflight.pieceReport.notPromoted +
    preflight.pieceReport.orphan
  const totalCount =
    articles.length +
    jurisprudence.length +
    caseSources.faits.length +
    caseSources.pieces.length

  // Project a live SourceResult onto the SourceCard shape + wire its "+ Citer"
  // to the insert flow. Non-citable hits (no refKey) and in-flight inserts
  // disable the button. (Same projection the mockup pane did — presentation.)
  function toCard(r: SourceResult): SourceCardData {
    return {
      kind: r.kind === 'article' ? 'articles' : 'jurisp',
      name: r.name,
      meta: r.meta,
      flag: flagOf(r.countryCode),
      snip: r.snippet,
      ecli: r.ecli ?? undefined,
      qual: r.qual,
      citeDisabled: r.refKey === null || inserting,
      onCite: () => void cite(r),
    }
  }

  // Faits stay DISPLAY-ONLY: a fait is not an authority, so it never enters
  // the citation firewall — `citeDisabled` + no `onCite` → no "+ Citer".
  function factToCard(f: CaseFact): SourceCardData {
    const statusLabel: Record<FactStatus, string> = {
      draft: t('sources.factStatus.draft'),
      to_validate: t('sources.factStatus.to_validate'),
      established: t('sources.factStatus.established'),
    }
    return {
      kind: 'faits',
      name: f.category ?? t('sources.group.faits'),
      meta: statusLabel[f.status],
      snip: f.text,
      citeDisabled: true,
    }
  }

  // Pièces ARE citable (F1): the BE supplies (party, ordinal); the card shows
  // « Pièce n° N » (rendered from those) and "+ Citer" inserts a chip anchored
  // to the exhibit UUID via `citePiece`. The BE owns the number (W1).
  function pieceToCard(p: PieceRef): SourceCardData {
    return {
      kind: 'pieces',
      name: renderPieceLabel(p.party, p.ordinal, language),
      meta: p.label,
      snip: '',
      citeDisabled: inserting,
      onCite: () => void citePiece(p),
    }
  }

  const onSearch = (q: string): void => {
    void search(q)
    caseSources.setQuery(q)
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-col gap-2 px-3 py-2">
        <SourceSearchBox onSearch={onSearch} />

        {unverified > 0 && (
          <div
            role="status"
            data-testid="sources-unverified-banner"
            className="rounded-md border-s-2 border-warn bg-warn/5 px-2.5 py-1.5 text-[11px] font-semibold text-ink/80"
          >
            {t('sources.unverified', { count: unverified })}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3">
        {loading && (
          <div className="text-xs text-ink/60" data-testid="sources-loading">
            {t('sources.loading')}
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="sources-error"
            className="rounded-md border-s-2 border-warn bg-warn/5 px-2.5 py-1.5 text-[11px] text-ink/80"
          >
            {error.message}
          </div>
        )}

        {!loading && !error && !hasAnything && (
          <div className="text-xs text-ink/60" data-testid="sources-empty">
            {t('sources.empty')}
          </div>
        )}

        {!loading && articles.length > 0 && (
          <SourceGroup
            testId="sources-group-articles"
            title={t('sources.group.articles')}
            count={t('sources.resultCount', { count: articles.length })}
          >
            {articles.map((r) => (
              <SourceCard key={`a-${r.id}`} {...toCard(r)} />
            ))}
          </SourceGroup>
        )}

        {!loading && jurisprudence.length > 0 && (
          <SourceGroup
            testId="sources-group-jurisprudence"
            title={t('sources.group.jurisprudence')}
            count={t('sources.resultCount', { count: jurisprudence.length })}
          >
            {jurisprudence.map((r) => (
              <SourceCard key={`j-${r.id}`} {...toCard(r)} />
            ))}
          </SourceGroup>
        )}

        {caseSources.faits.length > 0 && (
          <SourceGroup
            testId="sources-group-faits"
            title={t('sources.group.faits')}
            count={t('sources.resultCount', { count: caseSources.faits.length })}
          >
            {caseSources.faits.map((f) => (
              <SourceCard key={`f-${f.id}`} {...factToCard(f)} />
            ))}
          </SourceGroup>
        )}

        {caseSources.pieces.length > 0 && (
          <SourceGroup
            testId="sources-group-pieces"
            title={t('sources.group.pieces')}
            count={t('sources.resultCount', {
              count: caseSources.pieces.length,
            })}
          >
            {caseSources.pieces.map((p) => (
              <SourceCard key={`p-${p.refId}`} {...pieceToCard(p)} />
            ))}
          </SourceGroup>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-ink/10 px-3 py-2">
        <span className="text-[10px] text-ink/60">
          {hasAnything
            ? t('sources.sourceCount', { count: totalCount })
            : t('sources.noSearch')}
        </span>
        <button
          type="button"
          data-testid="sources-verify-all"
          onClick={() => void preflight.run()}
          disabled={preflight.loading}
          className="rounded border border-ink/15 px-2 py-0.5 text-[10px] font-semibold text-ink hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {preflight.loading ? t('sources.verifying') : t('sources.verifyAll')}
        </button>
      </div>
    </div>
  )
}
